import { DatePipe, I18nPluralPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import CostViewModel from '@app/models-internal/cost-view-model';
import ScoutingDayRequest from '@app/models-internal/scouting-day-request';
import ScoutingDayResponse from '@app/models-internal/scouting-day-response';
import ScoutingDaySettingViewModel from '@app/models/scouting-day-setting-view-model';
import { NotificationService, PaymentService, UserService } from '@app/services';
import { Observable, Subject } from 'rxjs';

import { Listing, ReservationCost } from '../../models';

/**
 * Service for Listing Seasonal Rentals
 */
@Injectable()
export class ScoutingBookingService {
  listing: Listing;
  startDate: string;
  endDate: string;
  adults: number;
  children: number;
  participantCount: number;
  reservationCost: ReservationCost;

  private readonly _serviceName = 'ScoutingBookingService';

  private _stepCompleted: Subject<string>;

  /**
   * Constructor for SeasonalRentalService
   * @param http
   */
  constructor(
    private http: HttpClient,
    readonly notify: NotificationService,
    readonly userService: UserService,
    private datePipe: DatePipe,
    private pluralPipe: I18nPluralPipe,
    private paymentService: PaymentService
  ) {
    this._stepCompleted = new Subject<string>();
  }

  /*
   * The event will fire when a step in the rental booking process step has completed.
   * Example: once a user has succesfully paid for the rental
   */
  get stepCompleted() {
    return this._stepCompleted;
  }

  /**
   * Scouting Booking Step Categories
   */
  public get workflowStepCategories(): any {
    return {
      signin: 'Sign In',
      termsAndConditions: 'Land Owner<br>Terms &amp; Conditions',
      payment: 'Payment',
      backgroundCheck: 'Background<br />Check',
      complete: 'Complete',
      contactInfo: 'Contact Info'
    };
  }

  /**
   * list of all possible categories (routes) that a user might have to fill out listed in order
   */
  public get workflowSteps(): any {
    return [
      {
        route: 'signin',
        step: this.workflowStepCategories.signin,
        complete: false,
        showCosts: false
      },
      {
        route: 'contact-info',
        step: this.workflowStepCategories.contactInfo,
        complete: false,
        showCosts: false
      },
      {
        route: 'payment-terms',
        step: this.workflowStepCategories.termsAndConditions,
        complete: false,
        showCosts: false
      },
      {
        route: 'payment',
        step: this.workflowStepCategories.payment,
        complete: false,
        showCosts: true
      },
      {
        route: 'payment-result',
        step: this.workflowStepCategories.payment,
        complete: false,
        showCosts: true
      },
      {
        route: 'background-check',
        step: this.workflowStepCategories.backgroundCheck,
        complete: false,
        showCosts: false
      },
      {
        route: 'background-check-disclosure',
        step: this.workflowStepCategories.backgroundCheck,
        complete: false,
        showCosts: false
      },
      {
        route: 'background-check-auth',
        step: this.workflowStepCategories.backgroundCheck,
        complete: false,
        showCosts: false
      },
      {
        route: 'finished',
        step: this.workflowStepCategories.complete,
        complete: false,
        showCosts: false
      }
    ];
  }

  /**
   * Executes a purchase of a scouting day
   */
  public purchaseScoutingDay(request: ScoutingDayRequest) {
    // console.log(`purchase`,stripeSource);
    return this.http.post<ScoutingDayResponse>('scout/purchase', request);
  }

  /**
   * Gets a cost of a scouting day purchase
   */
  public getScoutingDayCost(request: ScoutingDayRequest) {
    // console.log(`purchase`,stripeSource);
    return this.http.post<CostViewModel>('scout/cost', request);
  }

  public getPublicScoutingDayCost(request: ScoutingDayRequest) {
    // console.log(`purchase`,stripeSource);
    return this.http.post<CostViewModel>('scout/cost', request);
  }


  /**
   * Gets all scouting day settings for the OA Platform
   */
  public getScoutingDaySettings(): Observable<ScoutingDaySettingViewModel[]> {
    return this.http.get<ScoutingDaySettingViewModel[]>('scout');
  }

  /*
    Adds a new scouting day
  */
  addScoutingDay(item: ScoutingDaySettingViewModel) {
    return this.http.post<ScoutingDaySettingViewModel>('scout', item);
  }

  /*
    Removes a new scouting day
  */
  removeScoutingDay(item: ScoutingDaySettingViewModel) {
    if (!item) {
      throw new Error('ScoutingDaySettingViewModel is null.');
    }

    return this.http.delete<ScoutingDaySettingViewModel>(`scout/${item.id}`);
  }
}
